import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { JobPostAutomationService } from '@employer/app/modules/jobs/views/job-automation/job-post-automation.service'
import { IFileReceipt } from '@engineering11/files-web'
import { E11DynamicFormBuilderComponent, E11DynamicFormModule, E11FormForType, e11LayoutGroup } from '@engineering11/ui-lib/e11-form-builder'
import { E11InputCheckboxModule } from '@engineering11/ui-lib/e11-input-checkbox'
import { IMarkdownFile } from '@engineering11/ui-lib/e11-markdown-editor'
import { E11NotificationsService } from '@engineering11/ui-lib/e11-notifications'
import { E11SwitchModule } from '@engineering11/ui-lib/e11-switch'
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip'
import { E11ErrorHandlerService, toE11Error } from '@engineering11/web-api-error'
import { TranslateModule } from '@ngx-translate/core'
import { IAutomationFormConfig, MultiMediaArgs, sendMessageForm } from '../automation-form-message'
import { validateFile } from '../automation.utils'
import { IMarkdownReceiptFile, ISendMessageFormData } from '../job-automation.model'
import { AutomationAttachmentPreviewComponent } from './automation-attachment-preview'
import { AutomationHeaderInfoComponent } from './automation-header-info.component'
import { AutomationLoaderComponent } from './automation-loader'
import { AutomationReminderInfoComponent } from './automation-reminder-info.component'
import { AutomationFormData, AutomationResetComponent, FormField } from './automation-reset.component'
import { AutomationTypeDisplayNames, AutomationUpgradePromptComponent } from './automation-upgrade-prompt.component'

@Component({
  selector: 'automation-message-form',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    ReactiveFormsModule,
    E11DynamicFormModule,
    E11SwitchModule,
    AutomationReminderInfoComponent,
    AutomationHeaderInfoComponent,
    AutomationResetComponent,
    AutomationAttachmentPreviewComponent,
    AutomationUpgradePromptComponent,
    AutomationLoaderComponent,
    E11InputCheckboxModule,
    E11TooltipModule,
  ],
  template: `
    <div>
      <automation-loader [isLoading]="isUploadingAttachment"></automation-loader>
      <div class="e11-flex e11-gap-2 e11-items-center">
        <h4 class="e11-font-bold" *ngIf="headerText">{{ headerText | translate }}</h4>
        <e11-switch
          *ngIf="showEnableSwitch"
          [checked]="initialValue?.enabled"
          (onChange)="onEnabledChange($event)"
          [size]="'xs'"
          [color]="'success'"
        ></e11-switch>
      </div>
      @if (fieldGroups) {
      <e11-dynamic-form-builder #formRef [loading]="loading" [fieldGroups]="fieldGroups" [initialValue]="initialValue" [hideSubmitMenu]="true">
        <ng-template e11Template="messageFootNoteTemplate">
          <ng-container [ngSwitch]="footNoteMessageType">
            <automation-reminder-info *ngSwitchCase="'reminder-info'"></automation-reminder-info>
            <span *ngSwitchDefault class="e11-block e11-my-2">{{ footNoteText ?? '' | translate }}</span>
          </ng-container>
        </ng-template>
        <ng-template e11Template="delayTimeHeaderTemplate">
          <automation-header-info
            [titleText]="delayTimeHeaderText ?? this.defaultDelayTimeHeaderText"
            [toolTipText]="delayTimeHeaderToolTipText ?? this.defaultDelayTimeHeaderToolTipText"
          ></automation-header-info>
          @if (showImmediateSwitch) {
          <div class="e11-flex e11-flex-col e11-gap-2 e11-mb-4">
            <div class="e11-flex e11-gap-2 e11-items-center">
              <e11-input-checkbox
                #immediateSwitchRef
                [checked]="_immediateValue ?? false"
                (change)="onImmediateChange($any($event).target.checked)"
                [size]="'xs'"
                [color]="'success'"
                [label]="'Send immediately' | translate"
              ></e11-input-checkbox>
              <e11-tooltip
                [color]="'dark-grey'"
                tooltipText="Using this option completely disables the “Initial Message”. No message wil be sent to the candidate. You can still choose where to place the candidate as they complete this step and automation will continue as configured."
              >
                <span class="material-icons-outlined e11-text-skin-base md-18">info</span>
              </e11-tooltip>
            </div>
            <p class="e11-text-sm e11-mb-0">
              {{
                "Let candidates choose to do this immediately as a 'next step' in the hiring process. This disables delay timing for this message."
                  | translate
              }}
            </p>
          </div>
          }
        </ng-template>
        <ng-template e11Template="upgradeProductTemplate">
          <automation-upgrade-prompt [automationType]="automationTypeDisplayName"></automation-upgrade-prompt>
        </ng-template>
        <ng-template e11Template="resetFormTemplate">
          <automation-reset
            *ngIf="showResetButton"
            [formField]="formFieldName"
            [loading]="loading"
            (formFieldReset)="formFieldReset($event)"
          ></automation-reset>
        </ng-template>
        <ng-template e11Template="videoUrlPreviewTemplate">
          <automation-attachment-preview [fileReceipts]="attachments" (removeFileReceipt)="onRemoveFile($event)"></automation-attachment-preview>
        </ng-template>
        <ng-template e11Template="newSwimlaneHeaderTemplate">
          <ng-content></ng-content>
        </ng-template>
        <ng-content></ng-content>
      </e11-dynamic-form-builder>
      }
    </div>
  `,
})
export class AutomationMessageFormComponent implements OnInit {
  defaultDelayTimeHeaderText = 'How long do we wait before sending this message?'
  defaultDelayTimeHeaderToolTipText = 'This is delayed from the time of application submission.'

  @ViewChild('formRef') formRef!: E11DynamicFormBuilderComponent<ISendMessageFormData>
  @Input({ required: true }) automationTypeDisplayName!: AutomationTypeDisplayNames
  @Input({ required: true }) formFieldName!: FormField
  @Input({ required: true }) formConfig?: IAutomationFormConfig
  @Input() headerText?: string
  @Input() loading = false
  @Input() showEnableSwitch = false
  _immediateValue?: boolean
  @Input() set immediateValue(value: boolean | undefined) {
    this._immediateValue = value ?? false
    this.onImmediateChange(this._immediateValue, false)
  }
  @Input() showImmediateSwitch = false
  @Input() set formData(data: ISendMessageFormData | undefined) {
    this.initialValue = data
    this.attachments = data?.attachments ?? []
  }
  @Input() showResetButton = false
  @Input() footNoteMessageType: 'text' | 'reminder-info' = 'text'
  @Input() footNoteText?: string
  @Input() delayTimeHeaderText?: string = this.defaultDelayTimeHeaderText
  @Input() delayTimeHeaderToolTipText?: string = this.defaultDelayTimeHeaderToolTipText
  @Input() extraFieldGroups?: ReturnType<typeof e11LayoutGroup<any>>[]

  @Output() valueChange = new EventEmitter<ISendMessageFormData>()
  @Output() immediateChange = new EventEmitter<boolean>()

  private errorHandler = inject(E11ErrorHandlerService)
  private notification = inject(E11NotificationsService)
  private cdr = inject(ChangeDetectorRef)
  private jobPostAutomationService = inject(JobPostAutomationService)
  initialValue?: ISendMessageFormData
  attachments: IFileReceipt[] = []

  isUploadingAttachment = false
  fieldGroups?: E11FormForType<ISendMessageFormData>

  ngOnInit() {
    this.fieldGroups = sendMessageForm(
      this.initialValue ?? {},
      this.formConfig ?? { senderOptions: [], canEditMessages: false },
      this.generateMultiMediaArgs(),
      ...(this.extraFieldGroups ?? [])
    )
    this.attachments = this.initialValue?.attachments || []
  }

  onEnabledChange(value: boolean) {
    this.formRef.form.get('enabled')?.setValue(value)
    this.emitChanges()
  }

  emitChanges() {
    this.valueChange.emit(this.value)
    this.cdr.detectChanges()
  }

  onRemoveFile(fileId: string) {
    this.removeFile(fileId)
  }

  formFieldReset(data: AutomationFormData) {
    this.formRef.reset(data as ISendMessageFormData)
  }

  onImmediateChange(value: boolean, shouldEmitChange: boolean = true) {
    if (!this.formRef || !this.formRef.form) return
    const delayDuration = this.formRef.form.get('delayDuration')
    const delayUnits = this.formRef.form.get('delayUnits')
    const senderId = this.formRef.form.get('senderId')

    if (value) {
      delayDuration?.setValue(0)
      delayDuration?.disable()
      delayUnits?.disable()
      senderId?.disable()
    } else {
      delayDuration?.enable()
      delayUnits?.enable()
      senderId?.enable()
    }

    if (shouldEmitChange) {
      this.immediateChange.emit(value)
    }
  }

  async handleFileUpload(markdownFiles: IMarkdownFile) {
    if (!markdownFiles) return

    const allAttachments = this.attachments || []
    const validation = validateFile([markdownFiles], allAttachments)

    if (!validation.status) {
      this.notification.popNotificationMessage({
        title: validation.title,
        message: validation.message,
        type: 'error',
      })
      return
    }
    this.isUploadingAttachment = true

    const fileReceipt = await this.uploadAttachment(markdownFiles)
    if (fileReceipt) {
      this.attachments.push(fileReceipt.receiptFile)
    }
    this.isUploadingAttachment = false
    this.emitChanges()
  }

  get value(): ISendMessageFormData {
    return {
      ...this.formRef.form.value,
      attachments: this.attachments,
    }
  }

  async uploadAttachment(file: IMarkdownFile): Promise<IMarkdownReceiptFile | undefined> {
    try {
      const attachment = await this.jobPostAutomationService.uploadAttachment(file.file)
      return { id: file.id, receiptFile: attachment }
    } catch (error) {
      this.errorHandler.handleE11Error(
        toE11Error(error, {
          title: 'Failed to upload attachment',
          type: 'upload-attachment-failed',
          additionalData: { file },
        }),
        { alertUser: true }
      )
      return undefined
    }
  }

  generateMultiMediaArgs(): Partial<MultiMediaArgs> {
    return {
      onFileUpload: (files: IMarkdownFile[]) => {
        this.handleFileUpload(files[files.length - 1])
      },
      onFileRemove: (file: IMarkdownFile) => {
        this.removeFile(file.id)
      },
    }
  }

  removeFile(fileId: string) {
    this.attachments = this.attachments.filter(f => f.id !== fileId)
    this.emitChanges()
  }
}
